import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'
import { storageService } from '@/services/storage'
import { LoginCredentials, RegisterData, User, AuthTokens, ApiError } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const isAuthReady = ref(false)
  const error = ref<string | null>(null)

  // Singleton guards to prevent concurrent races
  let _checkAuthStatusPromise: Promise<void> | null = null
  let _refreshTokenPromise: Promise<boolean> | null = null

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  async function login(credentials: LoginCredentials): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)
      
      if (response.data && response.data.user && response.data.access && response.data.refresh) {
        const tokens = {
          access: response.data.access,
          refresh: response.data.refresh
        }
        await setAuthData(response.data.user, tokens)
        return true
      }
      
      throw new Error('Invalid response from server')
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Login failed'
      console.error('Login error:', apiError)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function register(userData: RegisterData): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.register(userData)
      
      if (response.data) {
        await setAuthData(response.data.user, response.data.tokens)
        return true
      }
      
      throw new Error('Invalid response from server')
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Registration failed'
      console.error('Registration error:', apiError)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    isLoading.value = true

    try {
      // Only try to logout via API if we have a valid refresh token
      // Don't call API if tokens are already expired (will cause 401 loop)
      if (refreshToken.value) {
        try {
          await authService.logout(refreshToken.value)
        } catch (err: any) {
          // Ignore 401 errors during logout - tokens are already invalid
          if (err?.status !== 401 && err?.status !== 403) {
            console.error('Logout error:', err)
          } else {
            console.log('Logout API call failed (tokens already invalid), proceeding with local cleanup')
          }
        }
      }
    } finally {
      await clearAuthData()
      isLoading.value = false
    }
  }

  async function refreshAuthToken(): Promise<boolean> {
    if (_refreshTokenPromise) return _refreshTokenPromise

    async function doRefresh(): Promise<boolean> {
      if (!refreshToken.value) {
        console.log('No refresh token available')
        await clearAuthData()
        return false
      }

      try {
        console.log('Attempting to refresh token...')
        const response = await authService.refreshToken(refreshToken.value)

        if (response.data?.access) {
          console.log('Token refresh successful')
          accessToken.value = response.data.access
          await storageService.updateAccessToken(response.data.access)
          return true
        }

        console.log('Invalid refresh response - no access token in response')
        throw new Error('Invalid refresh response')
      } catch (err: any) {
        console.error('Token refresh error:', err)

        // Check if it's a network error - don't clear auth data for network issues
        const isNetworkError = err?.message?.includes('Network error') ||
                             err?.message?.includes('NETWORK_ERROR') ||
                             err?.code === 'NETWORK_ERROR'

        // Check if it's a genuine auth error (401/403) - invalid/expired tokens
        const isAuthError = err?.status === 401 ||
                           err?.status === 403 ||
                           err?.code === 'token_not_valid'

        console.log('Error analysis:', { isNetworkError, isAuthError, status: err?.status })

        if (isAuthError) {
          // Only clear auth for genuine token rejection
          console.log('Clearing auth data due to invalid tokens')
          await clearAuthData()
        } else {
          // Network errors, 500s, timeouts — keep auth data, user is still logged in
          console.log('Token refresh failed (' + (isNetworkError ? 'network' : 'server') + ' error) - keeping auth data')
        }

        return false
      }
    }

    _refreshTokenPromise = doRefresh()
    try {
      return await _refreshTokenPromise
    } finally {
      _refreshTokenPromise = null
    }
  }

  async function checkAuthStatus(): Promise<void> {
    if (_checkAuthStatusPromise) return _checkAuthStatusPromise

    async function doCheck(): Promise<void> {
      isLoading.value = true

      try {
        console.log('AuthStore: Checking authentication status...')

        // Try to load tokens from storage
        const tokens = await storageService.getTokens()
        const userData = await storageService.getUserData()

        console.log('AuthStore: Retrieved from storage:', {
          hasTokens: !!tokens,
          hasUserData: !!userData,
          accessToken: tokens?.access ? 'present' : 'missing',
          refreshToken: tokens?.refresh ? 'present' : 'missing'
        })

        if (tokens && userData) {
          accessToken.value = tokens.access
          refreshToken.value = tokens.refresh
          user.value = userData

          console.log('AuthStore: Tokens and user data loaded, verifying with server...')

          // Verify token is still valid by fetching current user
          try {
            const response = await authService.getCurrentUser()
            if (response.data) {
              user.value = response.data
              await storageService.storeUserData(response.data)
              console.log('AuthStore: User verification successful')
            }
          } catch (err: any) {
            console.log('AuthStore: User verification failed, checking error type...')

            // Check if it's a network error (backend down) vs auth error
            const isNetworkError = err.message?.includes('Network error') || err.message?.includes('NETWORK_ERROR')

            if (isNetworkError) {
              console.log('AuthStore: Network error detected - continuing with cached auth data (offline mode)')
              // Keep existing auth state for offline usage
            } else {
              console.log('AuthStore: Authentication error - attempting token refresh...')
              // If it's an auth error (not network), try to refresh token
              const refreshed = await refreshAuthToken()
              if (!refreshed) {
                console.log('AuthStore: Token refresh failed, clearing auth data')
                await clearAuthData()
              } else {
                console.log('AuthStore: Token refresh successful')
              }
            }
          }
        } else {
          console.log('AuthStore: No tokens or user data found in storage')
        }
      } catch (err) {
        console.error('Check auth status error:', err)
        await clearAuthData()
      } finally {
        isLoading.value = false
        isAuthReady.value = true
        console.log('AuthStore: Final auth state:', {
          isAuthenticated: isAuthenticated.value,
          hasUser: !!user.value,
          hasAccessToken: !!accessToken.value
        })
      }
    }

    _checkAuthStatusPromise = doCheck()
    try {
      await _checkAuthStatusPromise
    } finally {
      _checkAuthStatusPromise = null
    }
  }

  async function updateProfile(profileData: Partial<User>): Promise<boolean> {
    if (!isAuthenticated.value) return false

    isLoading.value = true
    error.value = null

    try {
      const response = await authService.updateProfile(profileData)
      
      if (response.data) {
        user.value = response.data
        await storageService.storeUserData(response.data)
        return true
      }
      
      throw new Error('Failed to update profile')
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Profile update failed'
      console.error('Profile update error:', apiError)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function updateAccessibilityPreferences(preferences: any): Promise<boolean> {
    if (!isAuthenticated.value || !user.value) return false

    try {
      // Optimistic update: Update local user data immediately
      const previousProfile = user.value.profile ? { ...user.value.profile } : null

      // Update user object with new preferences
      // Store preferences in profile.accessibility_preferences (not directly on profile)
      // Also add a flag to mark that preferences have been saved (for onboarding check)
      if (user.value.profile) {
        user.value.profile = {
          ...user.value.profile,
          accessibility_preferences: {
            ...(user.value.profile.accessibility_preferences || {}),
            ...preferences,
            _preferences_saved: true // Flag to indicate preferences have been saved
          }
        }
      } else {
        user.value.profile = {
          accessibility_preferences: {
            ...preferences,
            _preferences_saved: true // Flag to indicate preferences have been saved
          },
          tracking_enabled: true
        } as any
      }

      // Save to local storage immediately for instant persistence
      await storageService.storeUserData(user.value)

      // Background API sync (don't await - optimistic update)
      authService.updateAccessibilityPreferences(preferences)
        .then(async () => {
          console.log('Accessibility preferences synced to backend successfully')
          
          // Refresh user data to ensure profile is up to date
          // This ensures onboarding check works correctly after saving preferences
          try {
            const userResponse = await authService.getCurrentUser()
            if (userResponse.data) {
              user.value = userResponse.data
              await storageService.storeUserData(user.value)
            }
          } catch (refreshError) {
            console.warn('Failed to refresh user data after saving preferences:', refreshError)
            // Non-critical - preferences are already saved
          }
        })
        .catch(async (err: any) => {
          // Rollback on error
          console.error('Failed to sync accessibility preferences to backend:', err)

          // Restore previous profile data
          if (user.value && previousProfile) {
            user.value.profile = previousProfile
            await storageService.storeUserData(user.value)
          }

          error.value = 'Failed to sync preferences to server. Changes saved locally.'
        })

      // Return true immediately for instant UI feedback
      return true
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Failed to update accessibility preferences'
      console.error('Accessibility preferences update error:', apiError)
      return false
    }
  }

  async function requestPasswordReset(email: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      await authService.requestPasswordReset(email)
      return true
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Password reset request failed'
      console.error('Password reset request error:', apiError)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function confirmPasswordReset(uid: string, token: string, password: string, passwordConfirm: string): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      await authService.confirmPasswordReset(uid, token, password, passwordConfirm)
      return true
    } catch (err: any) {
      error.value = err.message || 'Password reset failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(
    oldPassword: string, 
    newPassword: string, 
    newPasswordConfirm: string
  ): Promise<boolean> {
    if (!isAuthenticated.value) return false

    isLoading.value = true
    error.value = null

    try {
      await authService.changePassword(oldPassword, newPassword, newPasswordConfirm)
      return true
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Password change failed'
      console.error('Password change error:', apiError)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function exportUserData(): Promise<any> {
    if (!isAuthenticated.value) return null

    try {
      const response = await authService.exportUserData()
      return response.data
    } catch (err: any) {
      const apiError = err as ApiError
      error.value = apiError.message || 'Data export failed'
      console.error('Data export error:', apiError)
      return null
    }
  }

  // Helper functions
  async function setAuthData(userData: User, tokens: AuthTokens): Promise<void> {
    user.value = userData
    accessToken.value = tokens.access
    refreshToken.value = tokens.refresh

    await Promise.all([
      storageService.storeTokens(tokens),
      storageService.storeUserData(userData)
    ])
  }

  async function clearAuthData(): Promise<void> {
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    error.value = null

    await Promise.all([
      storageService.clearTokens(),
      storageService.clearUserData()
    ])
  }

  function clearError(): void {
    error.value = null
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    isAuthReady,
    error,

    // Computed
    isAuthenticated,
    userName,
    userEmail,

    // Actions
    login,
    register,
    logout,
    refreshAuthToken,
    checkAuthStatus,
    updateProfile,
    updateAccessibilityPreferences,
    requestPasswordReset,
    confirmPasswordReset,
    changePassword,
    exportUserData,
    clearError,
    clearAuthData
  }
}, {
  persist: {
    key: 'matt-auth',
    storage: {
      getItem: (key: string): string | null => {
        try {
          // Use localStorage for web compatibility
          const item = localStorage.getItem(key)
          return item
        } catch {
          return null
        }
      },
      setItem: (key: string, value: string): void => {
        try {
          // Use localStorage for web compatibility
          localStorage.setItem(key, value)
        } catch {
          // Ignore storage errors
        }
      }
    },
    paths: ['user', 'accessToken', 'refreshToken']
  }
})